import type { SupabaseClient } from '@supabase/supabase-js'

import type { SupabaseResumeContent } from '@applicator/shared'

const COPY_SUFFIX = ' (copy)'
const MAX_TITLE_LENGTH = 200

export function buildDuplicateResumeTitle(sourceTitle: string): string {
  const base = sourceTitle.trim() || 'Resume'
  if (base.length + COPY_SUFFIX.length <= MAX_TITLE_LENGTH) {
    return `${base}${COPY_SUFFIX}`
  }
  return `${base.slice(0, MAX_TITLE_LENGTH - COPY_SUFFIX.length)}${COPY_SUFFIX}`
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export async function duplicateResume(
  supabase: SupabaseClient,
  params: { sourceResumeId: string; userId: string },
): Promise<{ id: string; title: string }> {
  const { sourceResumeId, userId } = params

  const { data: sourceResume, error: resumeError } = await supabase
    .from('resumes')
    .select('id, title')
    .eq('id', sourceResumeId)
    .eq('user_id', userId)
    .single()

  if (resumeError) throw resumeError
  if (!sourceResume) throw new Error('Resume not found')

  const { data: newResume, error: createError } = await supabase
    .from('resumes')
    .insert({
      user_id: userId,
      title: buildDuplicateResumeTitle(sourceResume.title),
      is_template: false,
    })
    .select('id, title')
    .single()

  if (createError) throw createError
  if (!newResume) throw new Error('Failed to create duplicate resume')

  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('section_type, content, display_order')
    .eq('resume_id', sourceResumeId)

  if (contentError) throw contentError

  const rows = (contentRows ?? []) as Pick<
    SupabaseResumeContent,
    'section_type' | 'content' | 'display_order'
  >[]

  if (rows.length > 0) {
    const { error: insertContentError } = await supabase.from('resume_content').insert(
      rows.map((row) => ({
        resume_id: newResume.id,
        section_type: row.section_type,
        content: cloneJson(row.content),
        display_order: row.display_order,
      })),
    )

    if (insertContentError) throw insertContentError
  }

  return { id: newResume.id, title: newResume.title }
}
